import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import { HeadingOne } from "Common/Custom/HeadingOne";
import PageBody from "Common/Custom/PageBody";
import FormRenderer from "Common/Custom/FormRenderer";
import Card from "../../../Components/CardStucher";
import AddRoomBtn from "../../../Components/AddRoomBtn";
import ActionDropdown from "../../../Components/ActionDropdown";
import DeleteButton from "../../../Components/DeleteButton";
import CustomerRadio from "../../../Components/CustomerRadio";
import CustomerFormModal from "../../../Customer/CreateNewCustomerForm";
import useRoomChange from "../../../Components/useRoomChange";
import useBillingCalculation from "../../../Components/useBillingCalculation";
import useRoomTypeOptions from "../../../Components/useRoomTypeOptions";
import ExtraFacilitySection from "../../../Components/ExtraFacilitySection";
import { RoomInfoFields, RentInfoFields } from "../../../Components/RoomInfoFields";
import ParkingBookingSection from "../../../Components/ParkingBookingSection";
import AdvanceDetailsList from "../../../Components/AdvanceDetailsList"
import BillingDetails from "../../../Components/BillingDetails"
import useCustomerBalance from "../../../Components/useCustomerBalance"
import RoomBlockSkeleton from "../../../Components/RoomBlockSkeleton"


import {
    getCombinedRoomInfo
} from "adminApi/RoomBookingApi/RoomBooking";

import {
    deleteCustomerHelper,
    enrichRoom,
} from "../../../HELPERS/Helpers";

const RoomBookingForm = ({
    values = {},
    errors = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    setValues = () => { },
    roomTypesOption = [],
    paymentMethodTypesOption = [],
    taxConfig = {},
    initialValues = {},
    form_data = {},
    ChildrenMinAge = null,
    ChildrenMaxAge = null,
    roomFields = [],
    emptyPaymentDetail = [],
    setChildrenMinAge = () => { },
    setChildrenMaxAge = () => { },
    setErrors = {},
    validateField = {},
    DormitoryRules = {},
    RoomFieldsRules = {},
    AdvanceDetailsRules = {},
    emptyRoom
}) => {

    // Customer modal
    const rootContainer = document.getElementById("root-container");

    const [show, setShow] = useState(false);
    const [title, setTitle] = useState("");
    const [customerType, setCustomerType] = useState("");
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState({});
    const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" });
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [roomNumbersByType, setRoomNumbersByType] = useState({});
    const [complementryByType, setComplementryByType] = useState({});
    const [roomTypeInfoByType, setRoomTypeInfoByType] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(0);
    const [showBill, setShowBill] = useState(false);
    const [billOpenIndex, setBillOpenIndex] = useState(null);

    const isInitializedRef = useRef(false);
    const roomsLoadedRef = useRef(false);
    const previousRoomsRef = useRef();
    const customerAutoSelectedRef = useRef(false);

    const PreveBookedRoomIds = rooms?.map(r => r.book_details_id).filter(Boolean) || [];

    // Room Type Options Hook
    const { ensureTypeOptions, isLoading: isLoadingRooms } = useRoomTypeOptions({
        roomNumbersByType,
        setRoomNumbersByType,
        setComplementryByType,
        setRoomTypeInfoByType,
        roomTypeInfoByType,
        values,
        setChildrenMinAge,
        setChildrenMaxAge,
        getCombinedRoomInfo
    });


    // Update Room
    const updateRoom = useCallback((index, patch) => {
        setRooms((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    }, []);

    // New Customer Modal
    useEffect(() => {
        if (!newCustomer?.customer_id) {
            return
        }
        setExistingCustomers((prev) => {
            return [...prev, newCustomer];
        });
        setShow(false)
    },
        [
            newCustomer,
            setNewCustomer
        ]
    );

    // Set Rooms — wait for roomTypesOption to enrich bed/person charges before recalculating
    useEffect(() => {
        if (values.rooms && values.rooms.length > 0 && !isInitializedRef.current && roomTypesOption.length > 0) {
            const enriched = values.rooms.map(room => {
                const typeDetails = roomTypeInfoByType[room.room_type_id] || roomTypesOption.find(rt => String(rt.room_type_id) === String(room.room_type_id));
                return enrichRoom(room, typeDetails, values);
            });
            setRooms(enriched);
            isInitializedRef.current = true;
        }
    },
        [
            values.rooms,
            roomTypesOption,
            roomTypeInfoByType,
            enrichRoom
        ]
    );

    // Set Existing Customers
    // Bug Fix 1: customer_phone can be null — removed it from the truthy gate
    // so the customer row is always populated when customer_id + full_guest_name exist
    useEffect(() => {
        if (
            values.customer_id &&
            values.full_guest_name &&
            existingCustomers.length === 0
        ) {
            const id = String(values.customer_id);
            const cust = {
                customer_id: id,
                full_guest_name: values.full_guest_name,
                country_dial_code: values.country_dial_code,
                mobile: values.mobile,
            };
            setExistingCustomers([cust]);
            setSelectedCustomerId(id);
        }
    },
        [
            values.customer_id,
            values.full_guest_name,
            values.mobile,
            existingCustomers.length,
            setExistingCustomers
        ]
    );

    // Set Room Types Options
    useEffect(() => {
        if (roomTypesOption.length > 0 && rooms.length > 0 && !roomsLoadedRef.current) {
            const loadRoomDetails = async () => {

                for (const room of rooms) {
                    if (room.room_type_id) {
                        await ensureTypeOptions(room.room_type_id, room);
                    }
                }
                roomsLoadedRef.current = true;
            };
            loadRoomDetails();
        }
    },
        [
            roomTypesOption.length,
            rooms.length,
            ensureTypeOptions
        ]
    );

    // Set new Rooms
    useEffect(() => {
        if (rooms.length > 0 && isInitializedRef.current) {
            const currentRoomsString = JSON.stringify(rooms);
            const previousRoomsString = previousRoomsRef.current;

            if (currentRoomsString !== previousRoomsString) {
                previousRoomsRef.current = currentRoomsString;

                setValues((prev) => {
                    if (JSON.stringify(prev.rooms) !== currentRoomsString) {
                        return { ...prev, rooms };
                    }
                    return prev;
                });
            }
        }
    },
        [
            rooms,
            setValues
        ]
    );

    // Billing Calculation Hook
    const { billingRates, computedRooms } = useBillingCalculation({ rooms, values, taxConfig, setValues });

    // Auto select customer
    useEffect(() => {
        if (!values.customer_id && existingCustomers.length === 1 && !customerAutoSelectedRef.current) {
            const id = String(existingCustomers[0].customer_id);
            setSelectedCustomerId(id);
            setValues((prev) => (
                {
                    ...prev,
                    customer_id: id,
                    full_guest_name: existingCustomers[0].full_guest_name,
                    mobile: existingCustomers[0].mobile,
                    country_dial_code: existingCustomers[0].country_dial_code
                }
            ));
            customerAutoSelectedRef.current = true;
        }
    },
        [
            existingCustomers,
            values.customer_id,
            setValues,
            newCustomer
        ]);

    useCustomerBalance({
        customerId: values?.customer_id,
        setValues,
        values
    });

    const [loadingRoomIndexes, setLoadingRoomIndexesState] = useState({});

    const setLoadingRooms = useCallback((index, isLoading) => {
        setLoadingRoomIndexesState(prev => ({ ...prev, [index]: isLoading }));
    }, []);

    // Handle Room Change
    // Handle Room Change
    const { handleRoomChange } = useRoomChange({
        rooms,
        roomTypesOption,
        roomTypeInfoByType,
        complementryByType,
        values,
        updateRoom,
        ensureTypeOptions,
        emptyRoom,
        setLoadingRooms
    });

    const onRoomFieldEvent = useCallback(
        (index) => (e) => {
            const { name, value } = e.target;

            handleRoomChange(index, name, value);

            console.log("name", name)
            console.log("value", value)
            console.log("index", index)

            const room = {
                ...values.rooms[index],
                [name]: value,
            };

            const roomRules =
                room.accommodation_type === "ROOM"
                    ? RoomFieldsRules
                    : DormitoryRules;

            if (roomRules[name]) {
                const errorMsg = validateField(
                    name,
                    value,
                    roomRules
                );

                setErrors((prev) => {
                    const roomErrors = [...(prev.rooms || [])];

                    roomErrors[index] = {
                        ...(roomErrors[index] || {}),
                        [name]: errorMsg,
                    };

                    return {
                        ...prev,
                        rooms: roomErrors,
                    };
                });
            }
        },
        [values.rooms, handleRoomChange, setErrors]
    );



    // Handle Customer Type Change
    const handleExited = () => {
        setCustomerType("");
        setSelectedCustomerId("");
        setNewCustomer([]);
    };

    const selectedRoomIds = useMemo(() => rooms.map(r => r.room_id).filter(Boolean), [rooms]);

    const addRoom = useCallback(() => setRooms((prev) => [initialValues.rooms[0], ...prev]), []);

    const removeRoom = useCallback((index) => setRooms((prev) => {
        return prev.length > 1 ? prev.filter((_, i) => i !== index) : prev;
    }), []);


    // destructure form data
    const {
        Reservation_Details,
        RoomFields,
        Advance_Details,
        extraFields,
        Rent_Info,
        parkingFields
    } = form_data

    const reservationDetails = Reservation_Details.map((item) => {
        if (item.name === "checkin_date") {
            return {
                ...item,
                disablePast: true,
            };
        }

        if (item.name === "checkout_date") {
            return {
                ...item,
                minDate: new Date(new Date(values.checkin_date).getTime() + 1 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                disablePast: true,
                maxDate: values.checkin_date ? new Date(new Date(values.checkin_date).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] : null, // Limit to 30 days after check-in
            };
        }

        return item;
    });

    const complementaryLabel = (room, roomData) => {
        const price = Number(roomData.complementary_price || 0);
        const days = Number(roomData.facilityDays || 0);
        const beds = Number(room.no_of_bed || 0);

        if (room.complementary_price_type === "DAY_WISE") {
            return room.accommodation_type === "DORMITORY"
                ? `${room.complementary_name} (${price.toFixed(2)} × ${beds} × ${days} )`
                : `${room.complementary_name} (${price.toFixed(2)} × ${days})`;
        }

        return room.accommodation_type === "DORMITORY"
            ? `${room.complementary_name} (${price.toFixed(2)} × ${beds})`
            : room.complementary_name;
    };


    return (
        <React.Fragment>
            <PageBody
                card={false}
                rowClass="row g-3 row-deck room-checkin-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(e);
                    setIsSubmitted(isSubmitted + 1)
                }}
                headerchildren={
                    <HeadingOne>
                        Create Room Booking
                    </HeadingOne>
                }
            >

                {/*------------------------------------------------------------------------------
              // Reservation Details section--------------------------------------------------
             //------------------------------------------------------------------------------ */}
                <div className="col-12">
                    <Card heading="Reservation Details">
                        <FormRenderer
                            fields={reservationDetails}
                            values={values}
                            errors={errors}
                            handleChange={handleChange}
                            optionConfig={{}}
                        />
                    </Card>
                </div>

                {/*------------------------------------------------------------------------------
               // Room Details section --------------------------------------------------------
              //------------------------------------------------------------------------------ */}
                <div className="col-12">
                    <Card heading="Room Details" CardRowClass="row g-3 pb-3">
                        <div className="col-12 d-flex flex-column gap-3">
                            {rooms.map((room, index) => {
                                const showBill = billOpenIndex === index;
                                const setShowBill = (open) => setBillOpenIndex(open ? index : null);
                                const themeClass = index > 0
                                    ? room.accommodation_type === "DORMITORY"
                                        ? "booking-theme-dormitory"
                                        : room.accommodation_type === "ROOM"
                                            ? "booking-theme-room"
                                            : ""
                                    : "";

                                return (
                                    <div
                                        key={index}
                                        className={`room-block-card ${themeClass}`}
                                    >
                                        {/* ── Room Block Header ── */}
                                        <div className="room-block-header">
                                            <div className="room-badge">
                                                <span className="badge-num">{index + 1}</span>
                                                Room {index + 1}
                                            </div>
                                            {room.accommodation_type && (
                                                <span className={`room-type-indicator ${room.accommodation_type === "DORMITORY" ? "type-dormitory" : "type-room"}`}>
                                                    {room.accommodation_type}
                                                </span>
                                            )}
                                            <div className="room-actions">
                                                {index === 0 && (
                                                    <AddRoomBtn
                                                        values={values}
                                                        onClick={addRoom}
                                                        rooms={rooms}
                                                    />
                                                )}
                                                {rooms.length > 1 && (
                                                    <DeleteButton
                                                        disabled={false}
                                                        onClick={() => removeRoom(index)}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Room Block Body ── */}
                                        <div className="room-block-body">
                                            {
                                                loadingRoomIndexes[index] ? (<RoomBlockSkeleton index={index} />)
                                                    : room.accommodation_type === "DORMITORY" ? (
                                                        /* ════════ DORMITORY LAYOUT ════════ */
                                                        <div className="row g-3">
                                                            {/* Left: Room Info */}
                                                            <div className="col-xxl-8  col-md-12 col-12">
                                                                <div className="room-section">
                                                                    <div className="room-section-title">
                                                                        <i className="ti ti-bed section-icon" />
                                                                        Room Info
                                                                    </div>
                                                                    <div className="room-section-body">
                                                                        <div className="row g-3">
                                                                            <RoomInfoFields
                                                                                RoomFields={RoomFields}
                                                                                room={room}
                                                                                index={index}
                                                                                isSubmitted={isSubmitted}
                                                                                PreveBookedRoomIds={PreveBookedRoomIds}
                                                                                ChildrenMinAge={ChildrenMinAge}
                                                                                roomTypesOption={roomTypesOption}
                                                                                roomNumbersByType={roomNumbersByType}
                                                                                selectedRoomIds={selectedRoomIds}
                                                                                onRoomFieldEvent={onRoomFieldEvent}
                                                                                isLoadingRooms={isLoadingRooms}
                                                                                values={values}
                                                                                errors={errors?.rooms}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Right: Room Rent Info */}
                                                            <div className="col-xxl-4  col-md-12 col-12">
                                                                <div className="room-section">
                                                                    <div className="room-section-title">
                                                                        <i className="ti ti-currency-rupee section-icon" />
                                                                        Room Rent Info
                                                                    </div>
                                                                    <div className="room-section-body">
                                                                        <div className="row g-2 small">
                                                                            <RentInfoFields
                                                                                Rent_Info={Rent_Info}
                                                                                room={room}
                                                                                index={index}
                                                                                rooms={rooms}
                                                                                errors={errors?.rooms}
                                                                                complementryByType={complementryByType}
                                                                                onRoomFieldEvent={onRoomFieldEvent}
                                                                            />

                                                                            {room?.room_type_id && room?.room_id && (
                                                                                <div className="col-12 mt-2">
                                                                                    <table className="table table-sm table-borderless mb-0">
                                                                                        <tbody>
                                                                                            <tr>
                                                                                                <td className="ps-0 text-muted">
                                                                                                    Bed Rate (
                                                                                                    {Number(room.rate_per_bed || 0).toFixed(2)}
                                                                                                    {" × "}
                                                                                                    {Number(room.no_of_bed || 0)}
                                                                                                    {" × "}
                                                                                                    {Number(computedRooms[index].facilityDays || 0)}
                                                                                                    )
                                                                                                </td>
                                                                                                <td className="pe-0 text-end">
                                                                                                    {(
                                                                                                        Number(room.rate_per_bed || 0) *
                                                                                                        Number(room.no_of_bed || 0) *
                                                                                                        Number(computedRooms[index].facilityDays || 0)
                                                                                                    ).toFixed(2)}
                                                                                                </td>
                                                                                            </tr>

                                                                                            {Number(room.complementary_price || 0) > 0 && (
                                                                                                <tr>
                                                                                                    <td className="ps-0 text-muted">
                                                                                                        {complementaryLabel(room, computedRooms[index])}
                                                                                                    </td>
                                                                                                    <td className="pe-0 text-end">
                                                                                                        {Number(computedRooms[index].complementary_total || 0).toFixed(2)}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            )}

                                                                                            <tr className="border-top fw-semibold">
                                                                                                <td className="ps-0">Dormitory Total</td>
                                                                                                <td className="pe-0 text-end">
                                                                                                    {Number(computedRooms[index].bed_rate_total || 0).toFixed(2)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : room.accommodation_type === "ROOM" ? (
                                                        /* ════════ ROOM LAYOUT ════════ */
                                                        <div className="d-flex flex-column gap-3">
                                                            {/* Row 1: Room Info Fields */}
                                                            <div className="room-section">
                                                                <div className="room-section-title">
                                                                    <i className="ti ti-door section-icon" />
                                                                    Room Info
                                                                </div>
                                                                <div className="room-section-body">
                                                                    <div className="row g-3">
                                                                        <RoomInfoFields
                                                                            RoomFields={RoomFields}
                                                                            room={room}
                                                                            index={index}
                                                                            isSubmitted={isSubmitted}
                                                                            PreveBookedRoomIds={PreveBookedRoomIds}
                                                                            ChildrenMinAge={ChildrenMinAge}
                                                                            roomTypesOption={roomTypesOption}
                                                                            roomNumbersByType={roomNumbersByType}
                                                                            selectedRoomIds={selectedRoomIds}
                                                                            onRoomFieldEvent={onRoomFieldEvent}
                                                                            values={values}
                                                                            errors={errors?.rooms}
                                                                            isLoadingRooms={isLoadingRooms}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Row 2: Extra Facility + Room Rent Info side by side */}
                                                            <div className="row g-3">
                                                                {/* Left: Extra Facility */}
                                                                <div className="col-xxl-8 col-12">
                                                                    <div className="room-section">
                                                                        <div className="room-section-title">
                                                                            <i className="ti ti-stars section-icon" />
                                                                            Extra Facility
                                                                        </div>
                                                                        <ExtraFacilitySection
                                                                            extraFields={extraFields}
                                                                            room={room}
                                                                            values={values}
                                                                            errors={errors?.rooms}
                                                                            index={index}
                                                                            onRoomFieldEvent={onRoomFieldEvent}
                                                                            ChildrenMinAge={ChildrenMinAge}
                                                                            ChildrenMaxAge={ChildrenMaxAge}
                                                                            as="div"
                                                                            showBill={showBill}
                                                                            setShowBill={setShowBill}

                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Right: Room Rent Info */}
                                                                <div className="col-xxl-4 col-12">
                                                                    <div className="room-section">
                                                                        <div className="room-section-title">
                                                                            <i className="ti ti-currency-rupee section-icon" />
                                                                            Room Rent Info
                                                                        </div>
                                                                        <div className="room-section-body">
                                                                            <div className="row g-2 small">
                                                                                <RentInfoFields
                                                                                    Rent_Info={Rent_Info}
                                                                                    room={room}
                                                                                    index={index}
                                                                                    rooms={rooms}
                                                                                    errors={errors?.rooms}
                                                                                    complementryByType={complementryByType}
                                                                                    onRoomFieldEvent={onRoomFieldEvent}
                                                                                    isLoadingRooms={isLoadingRooms}
                                                                                />

                                                                                {room?.room_type_id && room?.room_id && (
                                                                                    <div className="col-12 mt-2">
                                                                                        <table className="table table-sm table-borderless mb-0">
                                                                                            <tbody>
                                                                                                <tr>
                                                                                                    <td className="ps-0 text-muted">
                                                                                                        Room Rate (
                                                                                                        {Number(computedRooms[index].room_rate || 0).toFixed(2)}
                                                                                                        {" × "}
                                                                                                        {Number(computedRooms[index].facilityDays || 0)}
                                                                                                        )
                                                                                                    </td>
                                                                                                    <td className="pe-0 text-end">
                                                                                                        {Number(computedRooms[index].room_rate_total || 0).toFixed(2)}
                                                                                                    </td>
                                                                                                </tr>

                                                                                                {Number(computedRooms[index].extra_room_total || 0) > 0 && (
                                                                                                    <tr>
                                                                                                        <td className="ps-0 text-muted">
                                                                                                            Extra Facility Charges
                                                                                                            <a
                                                                                                                href="#"
                                                                                                                onClick={(e) => {
                                                                                                                    e.preventDefault();
                                                                                                                    setShowBill(!showBill);
                                                                                                                }}
                                                                                                                className="ms-2 text-decoration-underline"
                                                                                                            >
                                                                                                                Breakdown
                                                                                                            </a>
                                                                                                        </td>
                                                                                                        <td className="pe-0 text-end">
                                                                                                            {Number(computedRooms[index].extra_room_total || 0).toFixed(2)}
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                )}

                                                                                                {Number(room.complementary_price || 0) > 0 && (
                                                                                                    <tr>
                                                                                                        <td className="ps-0 text-muted">
                                                                                                            {room.complementary_price_type === "DAY_WISE"
                                                                                                                ? `${room.complementary_name} (${Number(computedRooms[index].complementary_price || 0).toFixed(2)} × ${computedRooms[index].facilityDays})`
                                                                                                                : room.complementary_name}
                                                                                                        </td>
                                                                                                        <td className="pe-0 text-end">
                                                                                                            {Number(computedRooms[index].complementary_total || 0).toFixed(2)}
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                )}

                                                                                                <tr className="border-top fw-semibold">
                                                                                                    <td className="ps-0">Room Total</td>
                                                                                                    <td className="pe-0 text-end">
                                                                                                        {Number(computedRooms[index].room_total || 0).toFixed(2)}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <RoomBlockSkeleton index={index} />
                                                    )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </div>

                {/*------------------------------------------------------------------------------
              // Customer Info section -------------------------------------------------------
              //------------------------------------------------------------------------------ */}
                <div className="col-xxl-7 col-lg-6 col-md-12 col-12">
                    <Card >
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                <div className="fw-semibold">Select / Add Customer</div>
                                <ActionDropdown
                                    setCustomerType={setCustomerType}
                                    setTitle={setTitle}
                                    setShow={setShow}
                                    IsGuestShow={false}
                                />
                            </div>

                            <div className="checkin-customer-list">
                                {existingCustomers?.length ? (
                                    existingCustomers.map((customer, i) => (
                                        <div
                                            key={i}
                                            className={`checkin-customer-row ${String(values.customer_id) === String(customer.customer_id) ? "selected" : ""}`}
                                            onClick={() => {
                                                setSelectedCustomerId(customer.customer_id);
                                                setValues(prev => ({
                                                    ...prev, customer_id: customer.customer_id
                                                }));
                                            }}
                                        >
                                            <div className="customer-radio">
                                                <CustomerRadio
                                                    name="customer_id"
                                                    value={customer.customer_id}
                                                    selectedValue={values.customer_id}
                                                    onChange={(id) => {
                                                        setSelectedCustomerId(id);
                                                        setValues(prev => ({
                                                            ...prev, customer_id: id
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <span className="customer-sl">{i + 1}</span>
                                            <div className="customer-info">
                                                <span className="customer-name">
                                                    {customer.full_guest_name || customer.name || "-"}
                                                </span>
                                                <span className="customer-mobile">
                                                    {customer.mobile ? (
                                                        <><i className="ti ti-phone" style={{ fontSize: 13 }} /> {customer.mobile} <span className="text-danger"> (Customer) </span></>
                                                    ) : "—"}
                                                </span>
                                            </div>
                                            <div className="customer-actions">
                                                <DeleteButton
                                                    disabled={existingCustomers.length === 1}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteCustomerHelper({
                                                            customerId: customer.customer_id,
                                                            setExistingCustomers,
                                                            setValues,
                                                            selectedCustomerId,
                                                            setSelectedCustomerId
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className={`checkin-customer-empty ${isSubmitted > 0 ? "has-error" : ""}`}>
                                        <i className="ti ti-user-plus empty-icon" />
                                        <span>Select Customer Details</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                {/*------------------------------------------------------------------------------
                // Parking Booking + Billing Details side by side-------------------------------
               //------------------------------------------------------------------------------ */}
                <div className="col-xxl-5 col-lg-6 col-md-12 col-12">
                    <ParkingBookingSection
                        values={values}
                        errors={errors}
                        handleChange={handleChange}
                        parkingFields={parkingFields}
                    />
                </div>

                {/*------------------------------------------------------------------------------
                    // Advance Details section------------------------------------------------------
                   //------------------------------------------------------------------------------ */}
                <div className="col-xxl-8  col-lg-12 col-12">
                    <AdvanceDetailsList
                        Advance_Details={Advance_Details}
                        paymentMethodTypesOption={paymentMethodTypesOption}
                        values={values}
                        setValues={setValues}
                        errors={errors}
                        setErrors={setErrors}
                        emptyPaymentDetail={emptyPaymentDetail}
                        validateField={validateField}
                        AdvanceDetailsRules={AdvanceDetailsRules}
                        FillAutoAdvanceAmount={true}
                    />
                </div>

                <div className="col-xxl-4 col-lg-5 col-md-6 col-12 ms-md-auto">
                    <BillingDetails values={values} setValues={setValues} billingRates={billingRates} />
                </div>

                <FormSubmitButton
                    label={"Submit"}
                    loading={loading}
                    colSize="col-12"
                    align="end"
                />
            </PageBody>

            {/* Customer Modal */}
            < CustomerFormModal
                show={show}
                setShow={setShow}
                handleExited={handleExited}
                title={title}
                customerType={customerType}
                rootContainer={rootContainer}
                selectedCustomerId={selectedCustomerId}
                setSelectedCustomerId={setSelectedCustomerId}
                setSelectedCustomer={setSelectedCustomer}
                setNewCustomer={setNewCustomer}
                setValues={setValues}
                selectedCustomer={selectedCustomer}
                newCustomer={newCustomer}
                setExistingCustomers={setExistingCustomers}
                setCustomerType={setCustomerType}
                setTitle={setTitle}
            />
        </React.Fragment >
    );
};

export default RoomBookingForm;